import { createTheme } from '@mui/material/styles';

const NAVY = '#1E3A5F';
const ACCENT = '#3B82F6';
const SUCCESS = '#10B981';
const DANGER = '#EF4444';
const WARNING = '#F59E0B';
const SURFACE = '#F8F9FB';
const BORDER = '#E5E7EB';
const TEXT_PRIMARY = '#111827';
const TEXT_SECONDARY = '#6B7280';

const theme = createTheme({
  palette: {
    primary: {
      main: NAVY,
      light: '#2E527F',
      dark: '#152A45',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: ACCENT,
      light: '#60A5FA',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    success: {
      main: SUCCESS,
      light: '#34D399',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    error: {
      main: DANGER,
      light: '#F87171',
      dark: '#DC2626',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: WARNING,
      light: '#FBBF24',
      dark: '#D97706',
      contrastText: '#FFFFFF',
    },
    info: {
      main: ACCENT,
      light: '#60A5FA',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    background: {
      default: SURFACE,
      paper: '#FFFFFF',
    },
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
    },
    divider: BORDER,
    action: {
      hover: '#F3F4F6',
      selected: '#EFF6FF',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '"DM Sans"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'sans-serif',
    ].join(','),
    fontSize: 14,
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' },
    h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.005em' },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5 },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },
  shape: { borderRadius: 8 },
  spacing: 4,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: SURFACE,
        },
        body: {
          backgroundColor: SURFACE,
          color: TEXT_PRIMARY,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '#root': {
          backgroundColor: SURFACE,
          minHeight: '100vh',
        },
        // Stop Chrome/Edge autofill from painting inputs salmon/yellow.
        'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active, textarea:-webkit-autofill, select:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 30px #FFFFFF inset !important',
          WebkitTextFillColor: `${TEXT_PRIMARY} !important`,
          caretColor: `${TEXT_PRIMARY} !important`,
          transition: 'background-color 5000s ease-in-out 0s',
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#D1D5DB',
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9CA3AF', backgroundClip: 'content-box' },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0,
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 16,
          paddingRight: 16,
          transition:
            'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 100ms ease',
          '&:active': { transform: 'scale(0.98)' },
          '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
        },
        sizeMedium: { minHeight: 40 },
        sizeSmall: { minHeight: 32, paddingTop: 4, paddingBottom: 4 },
        outlined: {
          borderWidth: 1,
          borderColor: BORDER,
          color: TEXT_PRIMARY,
          '&:hover': { borderWidth: 1, borderColor: '#D1D5DB', backgroundColor: '#F9FAFB' },
        },
        text: {
          color: TEXT_SECONDARY,
          '&:hover': { backgroundColor: '#F3F4F6', color: TEXT_PRIMARY },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 150ms ease',
          '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 8 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${BORDER}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          backgroundImage: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: 0,
          textTransform: 'none',
        },
        sizeSmall: { height: 22, fontSize: '0.7rem' },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          transition: 'border-color 150ms ease, box-shadow 150ms ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ACCENT,
            borderWidth: 1.5,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${ACCENT}1A`,
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: TEXT_PRIMARY,
          boxShadow: 'none',
          borderBottom: `1px solid ${BORDER}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', borderRight: 'none' },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: { borderCollapse: 'separate', borderSpacing: 0 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${BORDER}`,
          padding: '12px 16px',
          fontSize: '0.875rem',
          color: TEXT_PRIMARY,
        },
        head: {
          backgroundColor: '#F9FAFB',
          color: TEXT_SECONDARY,
          fontWeight: 600,
          fontSize: '0.6875rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          padding: '10px 16px',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit !important',
          '& .MuiTableSortLabel-icon': { fontSize: 14, opacity: 0.5 },
          '&.Mui-active .MuiTableSortLabel-icon': { color: NAVY, opacity: 1 },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow:
            '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          border: `1px solid ${BORDER}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: 0,
          padding: '16px 20px',
          borderBottom: `1px solid ${BORDER}`,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: 20 } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 20px',
          borderTop: `1px solid ${BORDER}`,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${BORDER}`, minHeight: 40 },
        indicator: { height: 2, backgroundColor: NAVY },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: 0,
          fontSize: '0.875rem',
          minHeight: 40,
          color: TEXT_SECONDARY,
          '&.Mui-selected': { color: NAVY, fontWeight: 600 },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: TEXT_PRIMARY,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px',
          borderRadius: 6,
        },
        arrow: { color: TEXT_PRIMARY },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 150ms ease, color 150ms ease',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${BORDER}` },
        toolbar: { paddingLeft: 16, paddingRight: 8, minHeight: 52 },
        selectLabel: { fontSize: '0.75rem', color: TEXT_SECONDARY },
        displayedRows: { fontSize: '0.75rem', color: TEXT_SECONDARY },
        select: { fontSize: '0.75rem' },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontSize: '0.8rem', fontWeight: 600 },
      },
    },
  },
});

export default theme;
